import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ChevronLeft, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SEOFields from "@/components/admin/SEOFields";
import { api } from "@/lib/api";
import { toast } from "sonner";

const DEPTS_FALLBACK = ["R&D", "Quality", "Clinical", "Regulatory", "Manufacturing", "Sales & Marketing", "Supply Chain", "Medical Affairs"];
const TYPES = ["Full-time", "Contract", "Internship", "Part-time"];

const empty = {
  title: "",
  department: "",
  sub_department: "",
  location: "",
  employment_type: "Full-time",
  experience_min: 0,
  experience_max: 0,
  salary_range: "",
  description: "",
  responsibilities: "",
  requirements: "",
  skills: "",
  is_active: true,
  valid_through: "",
  meta_title: "",
  meta_description: "",
  meta_keywords: "",
  og_image: "",
};

const toListString = (arr) => (Array.isArray(arr) ? arr.join("\n") : (arr || ""));
const toListArray = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);

export default function AdminJobEditor() {
  const { id } = useParams();
  const editing = !!id;
  const nav = useNavigate();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [deptDocs, setDeptDocs] = useState([]);

  useEffect(() => {
    api.get("/departments")
      .then((r) => { setDeptDocs(Array.isArray(r.data) ? r.data : []); })
      .catch(() => {});
  }, []);

  const parentDepts = deptDocs.filter((d) => !d.parent_id);
  // If no dynamic depts are loaded (fallback), still let user type. Otherwise use dropdown.
  const parentNames = parentDepts.length ? parentDepts.map((d) => d.name) : DEPTS_FALLBACK;
  const selectedParent = deptDocs.find((d) => d.name === form.department);
  const subs = selectedParent ? deptDocs.filter((d) => d.parent_id === selectedParent.id) : [];

  useEffect(() => {
    if (!editing) return;
    api.get(`/admin/jobs/${id}`).then((r) => {
      const j = r.data;
      setForm({
        ...empty,
        ...j,
        responsibilities: toListString(j.responsibilities),
        requirements: toListString(j.requirements),
        skills: (j.skills || []).join(", "),
        valid_through: j.valid_through || "",
      });
    }).finally(() => setLoading(false));
  }, [editing, id]);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      sub_department: form.sub_department === "__none__" ? "" : (form.sub_department || ""),
      experience_min: Number(form.experience_min) || 0,
      experience_max: Number(form.experience_max) || 0,
      responsibilities: toListArray(form.responsibilities),
      requirements: toListArray(form.requirements),
      skills: (form.skills || "").split(",").map((s) => s.trim()).filter(Boolean),
      valid_through: form.valid_through || null,
    };
    try {
      if (editing) {
        await api.patch(`/admin/jobs/${id}`, payload);
        toast.success("Job updated");
      } else {
        await api.post("/admin/jobs", payload);
        toast.success("Job created");
      }
      nav("/admin/jobs");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="max-w-4xl">
      <Link to="/admin/jobs" className="mb-4 inline-flex items-center gap-1 text-sm text-slate-600 hover:text-primary"><ChevronLeft className="h-4 w-4" /> All jobs</Link>
      <h1 className="font-heading text-2xl font-semibold text-slate-900">{editing ? "Edit job" : "New job"}</h1>
      <form onSubmit={save} className="mt-6 space-y-5 rounded-xl border border-slate-200 bg-white p-6" data-testid="admin-job-editor">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label htmlFor="j-title">Title *</Label>
            <Input id="j-title" required value={form.title} onChange={onChange("title")} data-testid="job-form-title" />
          </div>
          <div>
            <Label>Department (Category)</Label>
            <Select value={form.department || ""} onValueChange={(v) => setForm({ ...form, department: v, sub_department: "" })}>
              <SelectTrigger data-testid="job-form-department"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>{parentNames.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Sub-department {subs.length > 0 && <span className="text-xs text-slate-400">(optional)</span>}</Label>
            {subs.length > 0 ? (
              <Select value={form.sub_department || ""} onValueChange={(v) => setForm({ ...form, sub_department: v })}>
                <SelectTrigger data-testid="job-form-sub-department"><SelectValue placeholder="— none —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— none —</SelectItem>
                  {subs.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <Input
                value={form.sub_department}
                onChange={onChange("sub_department")}
                placeholder={selectedParent ? "No sub-departments defined for this category" : "Pick a category first"}
                disabled={!selectedParent}
                data-testid="job-form-sub-department-input"
              />
            )}
          </div>
          <div>
            <Label htmlFor="j-loc">Location *</Label>
            <Input id="j-loc" required value={form.location} onChange={onChange("location")} data-testid="job-form-location" />
          </div>
          <div>
            <Label>Employment type</Label>
            <Select value={form.employment_type} onValueChange={(v) => setForm({ ...form, employment_type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="j-sal">Salary range</Label>
            <Input id="j-sal" value={form.salary_range} onChange={onChange("salary_range")} placeholder="e.g. INR 12-18 LPA" />
          </div>
          <div>
            <Label htmlFor="j-exp-min">Experience min (yrs)</Label>
            <Input id="j-exp-min" type="number" min="0" value={form.experience_min} onChange={onChange("experience_min")} />
          </div>
          <div>
            <Label htmlFor="j-exp-max">Experience max (yrs)</Label>
            <Input id="j-exp-max" type="number" min="0" value={form.experience_max} onChange={onChange("experience_max")} />
          </div>
          <div>
            <Label htmlFor="j-valid">Valid through (ISO date)</Label>
            <Input id="j-valid" placeholder="2026-12-31" value={form.valid_through} onChange={onChange("valid_through")} />
          </div>
          <div className="flex items-end gap-3">
            <div>
              <Label className="block">Active</Label>
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="job-form-active" />
            </div>
          </div>
        </div>

        <div>
          <Label htmlFor="j-desc">Description *</Label>
          <Textarea id="j-desc" required rows={5} value={form.description} onChange={onChange("description")} />
        </div>
        <div>
          <Label htmlFor="j-resp">Responsibilities (one per line)</Label>
          <Textarea id="j-resp" rows={4} value={form.responsibilities} onChange={onChange("responsibilities")} />
        </div>
        <div>
          <Label htmlFor="j-req">Requirements (one per line)</Label>
          <Textarea id="j-req" rows={4} value={form.requirements} onChange={onChange("requirements")} />
        </div>
        <div>
          <Label htmlFor="j-skills">Skills (comma separated)</Label>
          <Input id="j-skills" value={form.skills} onChange={onChange("skills")} placeholder="OSD, QbD, Dissolution" />
        </div>

        <SEOFields
          values={{ meta_title: form.meta_title, meta_description: form.meta_description, meta_keywords: form.meta_keywords, og_image: form.og_image }}
          onChange={(v) => setForm({ ...form, ...v })}
          titlePlaceholder="Optional — defaults to the job title"
          descriptionPlaceholder="Optional — defaults to the job description"
          testIdPrefix="job-seo"
        />

        <div className="flex items-center justify-end gap-2 border-t pt-4">
          <Link to="/admin/jobs"><Button type="button" variant="outline">Cancel</Button></Link>
          <Button type="submit" disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="job-form-save"><Save className="mr-1 h-4 w-4" />{saving ? "Saving…" : "Save"}</Button>
        </div>
      </form>
    </div>
  );
}
